import { Button, Loading, Stepper } from "components";
import Drawer from "components/Drawer";
import { StepConfig } from "components/Stepper/types";
import { FormikErrors, FormikProps, FormikTouched, setNestedObjectValues, useFormik, validateYupSchema, yupToFormErrors } from "formik";
import { ApplicationDataHelper, ErrorHelper, ValidationHelper } from "helpers";
import { ApplicationStatus } from "models/types";
import { toast } from "react-toastify";

import { useCallback, useEffect, useMemo, useState } from "react";
import { useBeforeUnload, useBlocker, useNavigate } from "react-router-dom";

import { MOBILE_MINIMUM_WIDTH, useIsMobile } from "hooks/useIsMobile";
import useUserToken from "hooks/useUserToken";
import { Application } from "models/Application";
import { FeatureFlag } from "models/auth/types";
import { useSelector } from "react-redux";
import { ApplicationDocumentService } from "services/applicationDocument";
import { ApplicationService } from "services/applicationService";
import { ApplicationFormDocumentFolder, DocumentFolderType, DocumentFolderTypesAndFileExtensions, DocumentRequestedMoment } from "services/types";
import { RootState } from "store";
import * as Yup from "yup";
import { ApplicationDocumentsStep, ConcessionaireStep, ExemptionType } from "./steps";
import ApplicationDataStep, { applicantDataSchema, applicationDataSectionsFields, applicationVehicleSchema } from "./steps/ApplicationData";

import { applicationDocumentFolderSchema } from "./steps/ApplicationDocuments";
import ApplicationReview from "./steps/ApplicationReview";
import {
	ApplicantDocumentType,
	ApplicationData,
	ApplicationDocumentValidationStatus,
	ApplicationFields,
	ApplicationFormData,
	ApplicationStep,
	PersonTypeOptions
} from "./steps/types";

const initialValues: ApplicationFormData = {
	status: ApplicationStatus.RASCUNHO,
	concessionaire: null,
	tollStations: null,
	exemptionType: null,
	applicantData: {
		applicantType: PersonTypeOptions.physical,

		applicantAddressPostalCode: null,
		applicantAddressState: null,
		applicantAddressCity: null,
		applicantAddressNeighbourhood: null,
		applicantAddressStreet: null,
		applicantAddressNumber: null,
		applicantAddressComplement: null,

		applicantEmail: null,
		applicantName: null,
		applicantPhoneNumber: null,
		applicantDocument: null,
		applicantDocumentType: ApplicantDocumentType.CPF
	},
	applicationVehicle: {
		plate: null,
		renavamCode: null,
		numberOfAxis: null,
		vehicleBrand: null,
		vehicleModel: null,
		color: null,
		manufactureYear: null,
		modelYear: null,
		tagNumber: null,
		documentValidationStatus: ApplicationDocumentValidationStatus.NOT_VALIDATED,
		tagCompany: {
			id: "",
			name: "",
			code: ""
		},
		tagSelection: null,
		foundPlateTagsCount: 0,
		fieldsMissingFromApi: []
	},
	documentFolders: {}
};

const applicationStepsDisplay: Record<ApplicationStep, StepConfig> = {
	[ApplicationStep.ConcessionaireStep]: {
		hideStepsIndicator: false
	},
	[ApplicationStep.Exemption]: {
		hideStepsIndicator: false
	},
	[ApplicationStep.ApplicationDocuments]: {
		hideStepsIndicator: false
	},
	[ApplicationStep.ApplicationData]: {
		hideStepsIndicator: false
	},
	[ApplicationStep.ApplicationReview]: {
		hideStepsIndicator: true,
		nextStepLabel: "Enviar solicitação"
	}
};

function mergeDocumentFoldersAndPreserveErrors(
	foldersWithErrors: Record<string, ApplicationFormDocumentFolder>,
	cleanFolders: Record<string, ApplicationFormDocumentFolder>
): Record<string, ApplicationFormDocumentFolder> {
	const dirtyFolders = Object.fromEntries(
		Object.entries(cleanFolders).map(([folderTypeId, newFolder]): [string, ApplicationFormDocumentFolder] => [
			folderTypeId,
			{
				...newFolder,
				documents: [...foldersWithErrors[folderTypeId].documents.filter((doc) => !!doc.error), ...newFolder.documents]
			}
		])
	);

	const unsynchronizedFolders = Object.fromEntries(
		Object.entries(foldersWithErrors).map(([folderTypeId, folderWithError]): [string, ApplicationFormDocumentFolder] => {
			const newFolderWithErrorData = { ...folderWithError };
			delete newFolderWithErrorData.id;
			return [folderTypeId, newFolderWithErrorData];
		})
	);
	return { ...unsynchronizedFolders, ...dirtyFolders };
}

const validateFinishedConcessionaireStep = (formik: FormikProps<ApplicationFormData>) => !!formik.values.concessionaire;
const validateFinishedExemptionStep = (formik: FormikProps<ApplicationFormData>) => {
	const exemption = formik.values.exemptionType;
	return !!exemption;
};
const validateFinishedDataStep = (errors: FormikErrors<ApplicationFormData>) => {
	const errorsExceptDocuments = Object.keys(errors).reduce((acc: string[], key: any) => {
		if (key !== "documentFolders") {
			acc.push(key);
		}
		return acc;
	}, []);
	return errorsExceptDocuments.length === 0;
};

const validateDocumentCount = (formValues: ApplicationFormData, documentFolderTypes: DocumentFolderType[] | undefined) => {
	if (!documentFolderTypes || !formValues) return false;
	const formDocumentFolders = formValues.documentFolders;
	return documentFolderTypes.every((folderType) => {
		if (folderType.alwaysRequired) {
			return formDocumentFolders[folderType.id]?.documents.length >= folderType.minAmountDocuments;
		}
		return true;
	});
};

const validateRequiredDocumentTypeGroup = (formValues: ApplicationFormData, documentFolderTypes: DocumentFolderType[] | undefined) => {
	if (!documentFolderTypes || !formValues) return false;
	const requiredDocumentsByGroup = documentFolderTypes.reduce((acc, folderType) => {
		if (folderType.requiredDocumentTypeGroup) {
			acc.push(folderType);
		}
		return acc;
	}, [] as DocumentFolderType[]);
	if (requiredDocumentsByGroup.length === 0) return true;
	return requiredDocumentsByGroup.some((folderType) => formValues.documentFolders[folderType.id]?.documents.length > 0);
};

const validateFinishedDocumentStep = (
	formik: FormikProps<ApplicationFormData>,
	savedApplicationData: ApplicationFormData | null,
	documentFolderTypes: DocumentFolderType[] | undefined,
	hasDocumentUploadError: boolean,
	documentsValidated: boolean
) => {
	return (
		(!formik.errors.documentFolders || Object.keys(formik.errors.documentFolders).length === 0) &&
		!!documentFolderTypes &&
		validateDocumentCount(formik.values, documentFolderTypes) &&
		validateRequiredDocumentTypeGroup(formik.values, documentFolderTypes) &&
		(Object.entries(formik.values.documentFolders)?.length > 0 || Object.entries(savedApplicationData?.documentFolders ?? {}).length > 0) &&
		!hasDocumentUploadError &&
		documentsValidated
	);
};

const isStepCompleted = (
	step: ApplicationStep,
	formik: FormikProps<ApplicationFormData>,
	savedApplicationData: ApplicationFormData | null = null,
	documentFolderTypes: DocumentFolderType[] | undefined = undefined,
	hasDocumentUploadError = false,
	documentsValidated = false
): boolean => {
	if (step === ApplicationStep.ConcessionaireStep) return validateFinishedConcessionaireStep(formik);
	if (step === ApplicationStep.Exemption) return validateFinishedExemptionStep(formik);

	if (step === ApplicationStep.ApplicationDocuments) {
		return validateFinishedDocumentStep(formik, savedApplicationData, documentFolderTypes, hasDocumentUploadError, documentsValidated);
	}
	if (step === ApplicationStep.ApplicationData) return validateFinishedDataStep(formik.errors);

	if (step === ApplicationStep.ApplicationReview) return true;
	return false;
};

export default function NewApplication({
	savedApplicationData,
	setSavedApplicationData
}: Readonly<{
	savedApplicationData: ApplicationFormData | null;
	setSavedApplicationData: (newData: ApplicationFormData | null) => void;
}>) {
	const navigate = useNavigate();
	const tokenInfo = useUserToken();
	const isMobile = useIsMobile(MOBILE_MINIMUM_WIDTH.SMALL);
	const { user } = useSelector((state: RootState) => state.auth);

	const [formikHasValidated, setFormikHasValidated] = useState<boolean>(false);
	const [isLoadingFolderTypes, setIsLoadingFolderTypes] = useState(false);
	const [savedOnError, setSavedOnError] = useState<boolean>(false);

	const exemptionApplicationService = useMemo(ApplicationService.getInstance, []);
	const applicationDocumentService = useMemo(ApplicationDocumentService.getInstance, []);
	const [preservedTouched, setPreservedTouched] = useState<FormikTouched<ApplicationFormData>>({});
	const [preservedErrors, setPreservedErrors] = useState<FormikErrors<ApplicationFormData>>({});

	const applicationId = savedApplicationData?.id;
	const [documentFolderTypesData, setDocumentFolderTypesData] = useState<DocumentFolderTypesAndFileExtensions | null>(null);
	const documentFolderTypeValidatedByApi = useMemo(() => {
		if (!tokenInfo?.hasFeatureEnabled(FeatureFlag.VALIDATE_VEHICLE_DOCUMENT)) return [];
		return documentFolderTypesData?.documentFolderTypes.filter((folderType) => folderType.documentType.isValidatedByApi);
	}, [documentFolderTypesData]);

	const [isSavingDraft, setIsSavingDraft] = useState<boolean>(false);
	const [isValidatingDocument, setIsValidatingDocument] = useState<boolean>(false);
	const [validatedDocuments, setValidatedDocuments] = useState<{ [folderTypeId: string]: string[] }>({});

	const setInitialValidatedDocuments = (documentFolderTypes: DocumentFolderType[]) => {
		const initialValidatedDocuments: { [folderTypeId: string]: string[] } = {};

		documentFolderTypes.forEach((folderType) => {
			const documents = savedApplicationData?.documentFolders[folderType.id]?.documents;
			if (folderType.documentType.isValidatedByApi && documents && documents.length > 0) {
				const file = savedApplicationData?.documentFolders[folderType.id].documents[0];
				initialValidatedDocuments[folderType.id] = [
					...(savedApplicationData?.applicationVehicle.documentValidationStatus === ApplicationDocumentValidationStatus.VALIDATION_SUCCESS
						? [file!.blobName]
						: [])
				];
			}
		});
		setValidatedDocuments(initialValidatedDocuments);
	};

	const loadDocumentFolderTypes = useCallback(
		(_applicationId: string) => {
			setIsLoadingFolderTypes(true);
			applicationDocumentService
				.getDocumentFolderTypesByApplication(_applicationId, DocumentRequestedMoment.AT_START)
				.then((responseData) => {
					setDocumentFolderTypesData(responseData);
					setInitialValidatedDocuments(responseData.documentFolderTypes);
				})
				.catch((err) => toast.error(ErrorHelper.getResponseErrorMessage(err)))
				.finally(() => setIsLoadingFolderTypes(false));
		},
		[applicationDocumentService]
	);

	useEffect(() => {
		if (applicationId && savedApplicationData?.concessionaire && savedApplicationData.exemptionType?.id) {
			loadDocumentFolderTypes(applicationId);
		}
	}, [applicationId, savedApplicationData?.concessionaire?.id, savedApplicationData?.exemptionType?.id]);

	const emptyDocumentFolders = useMemo<Record<string, ApplicationFormDocumentFolder>>(() => {
		if (!documentFolderTypesData) return {};
		return Object.fromEntries(
			documentFolderTypesData.documentFolderTypes.map((folderType): [string, ApplicationFormDocumentFolder] => [
				folderType.id,
				{
					documents: [],
					exemptionDocumentFolderType: folderType.id
				}
			])
		);
	}, [documentFolderTypesData?.documentFolderTypes]);

	const initialDataWithDocumentFolders = useMemo<ApplicationFormData>(
		() => ({ ...initialValues, documentFolders: { ...emptyDocumentFolders } }),
		[emptyDocumentFolders]
	);
	const savedApplicationDataWithDocumentFolders = useMemo<ApplicationFormData | null>(
		() =>
			savedApplicationData && {
				...savedApplicationData,
				documentFolders: {
					...emptyDocumentFolders,
					...savedApplicationData?.documentFolders
				}
			},
		[savedApplicationData, emptyDocumentFolders]
	);

	const saveApplicationData = useCallback(
		async (data: ApplicationFormData) => {
			const apiCall = applicationId
				? exemptionApplicationService.editDraftApplication(applicationId, data)
				: exemptionApplicationService.createDraftApplication(data);

			return apiCall
				.then((responseData) => {
					const newData = responseData instanceof Application ? (responseData.props as ApplicationData) : responseData;
					const formattedResponseData = ApplicationDataHelper.formatResponseDataDocuments(newData);
					const formattedNewData: ApplicationFormData = {
						...data,
						...formattedResponseData,
						documentFolders: mergeDocumentFoldersAndPreserveErrors(data.documentFolders, formattedResponseData.documentFolders)
					};
					setSavedApplicationData(formattedNewData);
					return responseData;
				})
				.catch((err) => {
					setSavedApplicationData({ ...data });
					throw err;
				});
		},
		[applicationId, setSavedApplicationData]
	);

	const [isCommitting, setIsCommitting] = useState<boolean>(false);
	const commitApplication = useCallback(
		async (data: ApplicationFormData) => {
			if (!applicationId) {
				return Promise.resolve();
			}
			setIsCommitting(true);
			return exemptionApplicationService
				.commitApplication(applicationId, data)
				.then((response) => {
					setSavedApplicationData({
						...savedApplicationData,
						applicationProcessCode: response.application.applicationProcessCode
					});
					navigate("./sucesso", { replace: true });
				})
				.catch(() => toast.error("Erro ao enviar solicitação. Verifique os dados preenchidos."))
				.finally(() => setIsCommitting(false));
		},
		[applicationId, exemptionApplicationService, setSavedApplicationData, navigate]
	);

	const folderTypeById: Record<string, DocumentFolderType> = useMemo(
		() => Object.fromEntries((documentFolderTypesData?.documentFolderTypes ?? []).map((folderType) => [folderType.id, folderType])),
		[documentFolderTypesData?.documentFolderTypes]
	);

	const validationSchema = useMemo(
		() =>
			Yup.object().shape({
				concessionaire: Yup.object().nullable().required("Campo obrigatório"),
				exemptionType: Yup.object().nullable().required("Selecione uma opção antes de continuar"),
				documentFolders: Yup.lazy((value) =>
					ValidationHelper.buildDocumentFoldersSchema(Object.keys(value), folderTypeById, applicationDocumentFolderSchema)
				),
				applicantData: applicantDataSchema,
				applicationVehicle: applicationVehicleSchema
			}),
		[folderTypeById]
	);

	const userFormData = useMemo<any>(() => {
		if (!user) return {};
		return {
			applicantData: {
				applicantName: user.name,
				applicantDocument: user.document,
				applicantPhoneNumber: user.contactNumber.toString(),
				applicantEmail: user.email
			}
		};
	}, [user]);

	const initialValuesForm = useMemo<ApplicationFormData>(() => {
		const applicantData = {
			...initialDataWithDocumentFolders.applicantData,
			...(!!savedApplicationDataWithDocumentFolders && { ...savedApplicationDataWithDocumentFolders.applicantData }),
			...(!!user && { ...userFormData.applicantData })
		};

		return {
			...initialDataWithDocumentFolders,
			...(!!savedApplicationDataWithDocumentFolders && { ...savedApplicationDataWithDocumentFolders }),
			...{ applicantData }
		};
	}, [savedApplicationDataWithDocumentFolders, userFormData, user]);

	const formik = useFormik({
		initialValues: initialValuesForm,
		validate: (values: ApplicationFormData) => {
			try {
				// Now we can access context values with `this.options.context`
				const context = { values };
				validateYupSchema(values, validationSchema, true, context);
			} catch (err) {
				setFormikHasValidated(true);
				return yupToFormErrors(err);
			}
			setFormikHasValidated(true);
			return {};
		},
		enableReinitialize: true,
		validateOnMount: true,
		validateOnBlur: true,
		initialTouched: preservedTouched,
		initialErrors: preservedErrors,
		onSubmit: saveApplicationData
	});
	const { setFieldValue, validateForm } = formik;

	useEffect(() => {
		validateForm();
	}, [validateForm, validationSchema]);

	useEffect(() => {
		const validSavedFolders = Object.fromEntries(
			Object.entries(savedApplicationDataWithDocumentFolders?.documentFolders ?? {}).filter(([folderTypeId]) => folderTypeId in folderTypeById)
		);
		setFieldValue("documentFolders", {
			...initialDataWithDocumentFolders.documentFolders,
			...validSavedFolders
		});
	}, [folderTypeById]);

	const [showSubmitWarning, setShowSubmitWarning] = useState<boolean>(false);

	const checkValidatedDocuments = useCallback(() => {
		if (
			!tokenInfo?.hasFeatureEnabled(FeatureFlag.VALIDATE_VEHICLE_DOCUMENT) ||
			savedApplicationData?.applicationVehicle.documentValidationStatus === ApplicationDocumentValidationStatus.API_UNAVAILABLE
		)
			return true;

		const documentFolderTypes = documentFolderTypesData?.documentFolderTypes;
		if (!documentFolderTypes) return false;

		return documentFolderTypes.every((folderType) => {
			if (!folderType.documentType.isValidatedByApi || !folderType.alwaysRequired) return true;
			const documents = formik.values.documentFolders[folderType.id]?.documents;
			const validatedDocumentsForFolder = validatedDocuments[folderType.id] || [];
			return documents?.length === 1 && validatedDocumentsForFolder.includes(documents[0].blobName);
		});
	}, [documentFolderTypesData?.documentFolderTypes, formik.values.documentFolders, validatedDocuments]);

	const initialStep: ApplicationStep | undefined = useMemo(() => {
		if (Object.entries(documentFolderTypesData ?? {}).length === 0 && formik.values.concessionaire && formik.values.exemptionType) {
			return undefined;
		}
		if (!isStepCompleted(ApplicationStep.ConcessionaireStep, formik)) {
			return ApplicationStep.ConcessionaireStep;
		}
		if (!isStepCompleted(ApplicationStep.Exemption, formik)) {
			return ApplicationStep.Exemption;
		}
		if (
			!isStepCompleted(
				ApplicationStep.ApplicationDocuments,
				formik,
				savedApplicationData,
				documentFolderTypesData?.documentFolderTypes,
				false,
				checkValidatedDocuments()
			)
		) {
			return ApplicationStep.ApplicationDocuments;
		}
		return ApplicationStep.ApplicationData;
	}, [formik.errors, formik.values]);
	const [currentStep, setCurrentStep] = useState<ApplicationStep>();
	const handlePrev = useCallback(() => {
		if (!currentStep) {
			navigate(-1);
		} else {
			setCurrentStep(currentStep - 1);
		}
	}, [currentStep, navigate]);

	const saveDraftApplication = useCallback(
		(values: ApplicationFormData) => {
			setIsSavingDraft(true);
			if (!values.concessionaire) return Promise.resolve();
			const newPreservedErrors = { ...formik.errors };
			const newPreservedTouched = { ...formik.touched };
			return saveApplicationData(values)
				.then((responseData) => {
					setIsSavingDraft(false);
					setPreservedErrors(newPreservedErrors);
					setPreservedTouched(newPreservedTouched);
					return responseData;
				})
				.catch((err: any) => {
					setIsSavingDraft(false);
					toast.error(ErrorHelper.getResponseErrorMessage(err));
				});
		},
		[formik.errors, formik.touched, formik.values, saveApplicationData]
	);

	useEffect(() => {
		formik.setTouched({});
	}, [currentStep]);

	const scrollToElement = (elementId: string) => {
		const section = document.querySelector(`#${elementId}`);
		if (!section) return;
		section.scrollIntoView({ behavior: "smooth", block: "start" });
	};

	const scrollToFirstDataFieldWithError = (errors: FormikErrors<ApplicationFormData>) => {
		const fieldsWithError = [...Object.keys(errors), ...(errors.applicantData ? Object.keys(errors.applicantData) : [])];
		Object.keys(applicationDataSectionsFields).every((section) => {
			const fields: string[] = applicationDataSectionsFields[section];
			return fields.every((field: string) => {
				if (fieldsWithError.includes(field)) {
					scrollToElement(`section-${section}`);
					return false;
				}
				return true;
			});
		});
	};

	const hasDocumentUploadError = useMemo<boolean>(() => {
		return !!Object.values(formik.values.documentFolders).find(
			(documentFolder) => !!documentFolder.documents.find((document) => !!document.error)
		);
	}, [formik.values.documentFolders]);

	const saveDraftAndProceed = (values: ApplicationFormData, checkDirty = true) => {
		if (!checkDirty || formik.dirty) {
			saveDraftApplication(values)
				.then(() => setCurrentStep(currentStep! + 1))
				.catch((err) => toast.error(ErrorHelper.getResponseErrorMessage(err)));
		} else {
			setCurrentStep(currentStep! + 1);
		}
	};

	const nextStepHandlers = useMemo<Record<ApplicationStep, () => void>>(() => {
		return {
			[ApplicationStep.ConcessionaireStep]: () => {
				if (validateFinishedConcessionaireStep(formik)) {
					saveDraftAndProceed(formik.values);
				} else {
					formik.setFieldTouched(ApplicationFields.concessionaire, true);
				}
			},
			[ApplicationStep.Exemption]: () => {
				if (validateFinishedExemptionStep(formik)) {
					saveDraftAndProceed({
						...formik.values,
						...(formik.values.exemptionType !== savedApplicationData?.exemptionType ? { ...{ documentFolders: {} } } : {})
					});
				} else {
					formik.setFieldTouched(ApplicationFields.exemptionType, true);
				}
			},
			[ApplicationStep.ApplicationDocuments]: async () => {
				const hasErrors = !validateFinishedDocumentStep(
					formik,
					savedApplicationData,
					documentFolderTypesData?.documentFolderTypes,
					hasDocumentUploadError,
					checkValidatedDocuments()
				);
				await formik.setTouched(setNestedObjectValues<FormikTouched<ApplicationFormData>>(formik.errors, true));
				if (hasErrors || hasDocumentUploadError) {
					const keysErrors = Object.keys(formik.errors.documentFolders || {});
					const firstError = keysErrors.length > 0 ? keysErrors[0] : "";
					scrollToElement(`document-folder-${firstError}`);
					const errorMessage = hasErrors ? "Preencha todos os arquivos obrigatórios para prosseguir" : "Verifique os arquivos anexados";
					toast.warn(errorMessage);
				} else {
					saveDraftAndProceed(formik.values, false);
				}
			},
			[ApplicationStep.ApplicationData]: async () => {
				const newErrors = await formik.validateForm();
				if (validateFinishedDataStep(newErrors)) {
					if (!isMobile) {
						// Mobile doesn't have a review step
						try {
							formik.submitForm();
							setCurrentStep(currentStep! + 1);
						} catch (err) {
							toast.error(ErrorHelper.getResponseErrorMessage(err));
						}
					} else {
						setShowSubmitWarning(true);
					}
				} else {
					await formik.setTouched(setNestedObjectValues<FormikTouched<ApplicationFormData>>(newErrors, true), false);
					scrollToFirstDataFieldWithError(newErrors);
					toast.warn("Preencha todos os campos obrigatórios para prosseguir");
				}
			},
			[ApplicationStep.ApplicationReview]: () => {
				commitApplication(formik.values);
			}
		};
	}, [currentStep, formik.values, formik.submitForm, formik.validateForm, checkValidatedDocuments]);

	useEffect(() => {
		if (!currentStep && currentStep !== 0 && formikHasValidated) {
			setCurrentStep(initialStep);
		}
	}, [initialStep, formikHasValidated]);

	const handleNext = currentStep || currentStep === 0 ? nextStepHandlers[currentStep] : () => {};

	const resetConcessionaireDependantFields = useCallback(() => {
		setFieldValue(ApplicationFields.exemptionType, null);
		setFieldValue(`${ApplicationFields.applicantData}`, {
			...initialValues.applicantData
		});
		setFieldValue(ApplicationFields.documentFolders, {});
		setFieldValue(ApplicationFields.concessionaire, null);
	}, [setFieldValue]);

	// botão de voltar do browser, clicar no header
	const blocker = useBlocker(({ currentLocation, nextLocation }) => formik.dirty && currentLocation.pathname !== nextLocation.pathname);
	useEffect(() => {
		if (blocker.state === "blocked") {
			// eslint-disable-next-line no-alert
			if (window.confirm("Você tem dados não salvos. Tem certeza que deseja sair?")) {
				blocker.proceed();
			}
		} else if (blocker.state === "proceeding") {
			navigate("/");
		}
	}, [blocker]);

	// F5, fechar a aba, mudar url na mão
	useBeforeUnload((event) => {
		if (formik.dirty) {
			event.preventDefault();
		}
	});

	const checkAndSaveDraftDocuments = (formikValues: ApplicationFormData) => {
		documentFolderTypeValidatedByApi?.forEach((folderType) => {
			const formikFolder = formikValues.documentFolders[folderType.id];
			if (!formikFolder || formikFolder.documents.length === 0) return;

			const file = formikFolder.documents[0];
			if (file?.id) return;

			// eslint-disable-next-line @typescript-eslint/no-use-before-define
			saveDraftAndValidateDocument(folderType.id);
		});
	};

	const saveDraftAndValidateDocument = async (documentTypeId: string) => {
		const applicationDraftData = await saveDraftApplication(formik.values);
		if (!applicationDraftData) return;

		const documentFolderToValidate = applicationDraftData?.documentFolders.find(
			(documentFolder) => documentFolder.exemptionDocumentFolderType === documentTypeId
		);
		if (!documentFolderToValidate || documentFolderToValidate.documents.length === 0) return;
		const documentToValidate = documentFolderToValidate.documents[0];

		try {
			setIsValidatingDocument(true);
			await applicationDocumentService.validateDocument(applicationDraftData.id!, documentToValidate.id!);
			setValidatedDocuments({
				...validatedDocuments,
				[documentTypeId]: [...(validatedDocuments[documentTypeId] || []), documentToValidate.blobName]
			});
		} catch (error: any) {
			if (error.response?.status === 500 && error.response?.data?.code === ErrorHelper.apiErrorCodes.EXTERNAL_API_UNREACHABLE) {
				toast.warn("Validação indisponível no momento. Tente novamente mais tarde ou preencha os dados manualmente.");
				await saveDraftApplication(formik.values);
				return;
			}

			// When the form is changed to set the document validation error, the useEffect below is triggered and it saves the draft, erasing the document
			const documentsWithoutThisError = documentFolderToValidate.documents.filter((doc) => doc.blobName !== documentToValidate.blobName);

			const documentsByFolderType = {
				documents: [
					...documentsWithoutThisError,
					{
						blobName: documentToValidate.name,
						name: documentToValidate.name,
						error: ErrorHelper.fileValidationErrorMessage
					}
				],
				exemptionDocumentFolderType: documentFolderToValidate.exemptionDocumentFolderType
			};

			if (tokenInfo?.hasFeatureEnabled(FeatureFlag.VALIDATE_VEHICLE_DOCUMENT)) {
				const formikValues = formik.values;
				formikValues.documentFolders[documentFolderToValidate.exemptionDocumentFolderType] = documentsByFolderType;

				checkAndSaveDraftDocuments(formikValues);
				setSavedOnError(true);
			}

			formik.setFieldValue(`documentFolders.${documentFolderToValidate.exemptionDocumentFolderType}`, documentsByFolderType);
			toast.error("Documento inválido, não será possível prosseguir com a solicitação.");
		} finally {
			setIsValidatingDocument(false);
		}
	};

	useEffect(() => {
		if (savedOnError) {
			setSavedOnError(false);
			return;
		}
		if (!tokenInfo?.hasFeatureEnabled(FeatureFlag.VALIDATE_VEHICLE_DOCUMENT)) return;

		checkAndSaveDraftDocuments(formik.values);
	}, [formik.values.documentFolders]);

	return (
		<div className="flex flex-col h-full">
			{currentStep || currentStep === 0 ? (
				<>
					<Stepper
						title="Nova solicitação"
						navigationButtonsEnabled={!isCommitting}
						currentStep={currentStep}
						handleNext={handleNext}
						handlePrev={handlePrev}
						stepsConfig={applicationStepsDisplay}
						navigationButtonsLoading={isSavingDraft || isValidatingDocument || isCommitting}
					>
						<ConcessionaireStep formik={formik} onConcessionaireChange={resetConcessionaireDependantFields} />
						<ExemptionType formik={formik} />
						<ApplicationDocumentsStep
							formik={formik}
							documentFolderTypesData={documentFolderTypesData}
							isLoadingFolderTypes={isLoadingFolderTypes}
							validatedDocuments={validatedDocuments}
							setValidatedDocuments={setValidatedDocuments}
						/>
						<ApplicationDataStep formik={formik} />
						<ApplicationReview formik={formik} documentFolderTypesData={documentFolderTypesData} />
					</Stepper>
					<Drawer showDrawer={showSubmitWarning}>
						<div className="flex flex-col gap-4">
							<strong className="text-base">Atenção</strong>
							<p className="text-sm">
								Após o envio da solicitação não será possível alterar as informações. Orientamos que revise os dados inseridos antes
								de prosseguir.
							</p>
						</div>
						<Button hierarchy="primary" onClick={() => setShowSubmitWarning(false)}>
							Revisar dados
						</Button>
						<Button hierarchy="secondary" type="submit" onClick={() => commitApplication(formik.values)} isLoading={isCommitting}>
							Enviar solicitação
						</Button>
					</Drawer>
				</>
			) : (
				<div className="mt-6">
					<Loading />
				</div>
			)}
		</div>
	);
}
